import type { NFTLayerAsset } from '../types';

// Cache for decoded images
const imageCache = new Map<string, HTMLImageElement>();
const loadingPromises = new Map<string, Promise<HTMLImageElement>>();

/**
 * Load a single image and cache it
 */
export const loadImage = (url: string): Promise<HTMLImageElement> => {
  // Return cached image if available
  if (imageCache.has(url)) {
    return Promise.resolve(imageCache.get(url)!);
  }

  // Return existing loading promise if already loading
  if (loadingPromises.has(url)) {
    return loadingPromises.get(url)!;
  }

  // Start new loading
  const promise = new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      imageCache.set(url, img);
      loadingPromises.delete(url);
      resolve(img);
    };
    img.onerror = () => {
      loadingPromises.delete(url);
      reject(new Error(`Failed to load image: ${url}`));
    };
    img.src = url;
  });

  loadingPromises.set(url, promise);
  return promise;
};

/**
 * Preload all layer images for given layer assets
 */
export const preloadLayerImages = async (layers: NFTLayerAsset[]): Promise<void> => {
  const promises = layers.map((layer) =>
    loadImage(layer.uri).catch((err) => {
      console.error(`Failed to preload layer ${layer.id}:`, err);
      return null;
    })
  );
  await Promise.all(promises);
};

/**
 * Get decoded image from cache synchronously
 */
export const getDecodedImageFromCache = (layerUri: string): HTMLImageElement | null => {
  return imageCache.get(layerUri) || null;
};

/**
 * Clear the image cache
 */
export const clearImageCache = (): void => {
  imageCache.clear();
  loadingPromises.clear();
};
